import {
  BaseActionObject,
  DoneInvokeEvent,
  StateNodeConfig,
  assign
} from "xstate4"
import {
  Context,
  LoadableSchema,
  StudyPlanTargets,
  Transitions
} from "../types"
import { CourseInfo } from "../../../../../types/gqlTypes/graphql"
import { getServiceErrorMessage } from "../helpers"
import { getSubjectPath } from "../../../study-plans/helpers"

export const getProgramCoursesTransition: StateNodeConfig<
  Context,
  LoadableSchema,
  Transitions,
  BaseActionObject
> = {
  id: "get_program_courses",
  initial: "loading",
  states: {
    loading: {
      entry: [assign<Context>({ loading: true })],
      exit: [assign<Context>({ loading: false })],
      invoke: {
        id: "getProgramCourses",
        src: "getProgramCourses",
        onDone: {
          target: "idle",
          actions: [
            assign<Context, DoneInvokeEvent<CourseInfo[]>>((ctx, evt) => {
              const availableCourses = evt.data || []
              let subject = ctx.subject

              // If we have an initial URL subject (old nav), find the matching course
              if (
                ctx.initialUrlSubject &&
                ctx.isOldNav &&
                availableCourses.length > 0
              ) {
                const matchingCourse = availableCourses.find(
                  course =>
                    getSubjectPath(course.name) === ctx.initialUrlSubject
                )
                if (matchingCourse) {
                  subject = matchingCourse.name
                }
              }

              return {
                availableCourses,
                subject
              }
            })
          ]
        },
        onError: {
          target: "idle",
          actions: [
            assign<Context, DoneInvokeEvent<CourseInfo[]>>({
              snackbar: (_ctx, _evt) => ({
                message: getServiceErrorMessage("getProgramCourses", {
                  messageData: ["get available courses"]
                }),
                withUndo: false,
                error: true
              })
            })
          ]
        }
      }
    },
    idle: {
      always: [
        // Transferring to the isolated Study Plan V2 UI
        {
          target: "#StudyPlan.study_plan_v2",
          cond: ctx => ctx.isStudyPlanV2Enabled
        },
        {
          target: "#StudyPlan.get_current_plan",
          cond: ctx => {
            return (
              ctx.currentTarget === StudyPlanTargets.ReviewPlan && !!ctx.subject
            )
          }
        },
        {
          // Handle edit page navigation - fetch current plan if no study plan exists
          target: "#StudyPlan.build_populated_study_plan_transition",
          cond: ctx => {
            return (
              ctx.currentTarget === StudyPlanTargets.Edit &&
              !!ctx.subject &&
              !ctx.studyPlan
            )
          }
        },
        {
          // Handle edit page navigation - go to edit if study plan exists
          // If study plan exists but the user changed the subject, we'll go to to study_plans_overview
          target: "#StudyPlan.edit_plan",
          cond: ctx => {
            return (
              ctx.currentTarget === StudyPlanTargets.Edit &&
              !!ctx.subject &&
              !!ctx.studyPlan &&
              ctx.studyPlan?.subject === ctx.subject
            )
          }
        },
        {
          // Check teacher role before checking for subject/settings
          // This ensures teachers go to teacher_view when currentTarget is null
          target: "#StudyPlan.teacher_view",
          cond: ctx => {
            return ctx.selfRefScope?.scope === "TEACHER"
          }
        },
        {
          target: "#StudyPlan.study_plans_overview",
          cond: ctx => {
            if (ctx.isOldNav) {
              return (
                ctx.currentTarget === StudyPlanTargets.Overview &&
                (!!ctx.subject || ctx.viewGlobalSettings)
              )
            } else {
              return !!ctx.subject || ctx.viewGlobalSettings
            }
          }
        },
        {
          target: "#StudyPlan.landing_view",
          cond: ctx => {
            return (
              ctx.selfRefScope?.scope === "DISTRICT" ||
              ctx.selfRefScope?.scope === "SCHOOL"
            )
          }
        },
        // Fallback for when no conditions match
        {
          target: "#StudyPlan.landing_view"
        }
      ]
    }
  }
}
